import { z } from 'zod'
import { parseBoolean } from '@library/utils/zod'

/**
 * 提供系統環境變數 Environment Variables
 */
export function useEnv() {
  /** 後端 API */
  const VITE_API_BASE_URL: string | undefined = z
    .string()
    .optional()
    .safeParse(import.meta.env.VITE_API_BASE_URL).data

  /** 前端 DEV 環境 VITE API Proxy 設定 */
  const VITE_API_PROXY_URL: string | undefined = z
    .string()
    .optional()
    .safeParse(import.meta.env.VITE_API_PROXY_URL).data

  /** 前端版本號 */
  const VITE_APPLICATION_VERSION: string | undefined = z
    .string()
    .optional()
    .safeParse(import.meta.env.VITE_APPLICATION_VERSION).data

  /**
   * 前端版本號 - 顯示方式
   *
   * hidden - 隱藏（display: none）
   * visible - 顯示
   * undefined - 不渲染 DOM 元素
   */
  const VITE_APPLICATION_VERSION_MODE: 'hidden' | 'visible' | undefined = z
    .union([z.literal('hidden'), z.literal('visible')])
    .optional()
    .safeParse(import.meta.env.VITE_APPLICATION_VERSION_MODE).data

  /** Base Url */
  const VITE_BASE_URL: string = import.meta.env.BASE_URL.endsWith('/')
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`

  /**
   * 是否於 console 顯示 ZOD 錯誤
   * always - 顯示
   * query - 網址列上有 query debug=true 時才顯示
   * never - 不顯示
   */
  const VITE_SHOW_ZOD_ERROR: 'always' | 'query' | 'never' | undefined = z
    .union([z.literal('always'), z.literal('query'), z.literal('never')])
    .optional()
    .safeParse(import.meta.env.VITE_SHOW_ZOD_ERROR).data

  /** 是否顯示 1A 錯誤 */
  const VITE_SHOW_1A_ERROR: boolean | undefined = parseBoolean(
    import.meta.env.VITE_SHOW_1A_ERROR
  )

  /** 是否自動帶入驗證碼 */
  const VITE_AUTO_INPUT_CAPTCHA_CODE: boolean | undefined = parseBoolean(
    import.meta.env.VITE_AUTO_INPUT_CAPTCHA_CODE
  )

  /** 儲存在瀏覽器的資料 Key 前綴 */
  const VITE_STORAGE_PREFIX_KEY: string | undefined = z
    .string()
    .optional()
    .safeParse(import.meta.env.VITE_STORAGE_PREFIX_KEY).data

  /** 是否開啟 vue-plugin-claw-guard */
  const VITE_ENABLE_VUE_PLUGIN_CLAW_GUARD: boolean | undefined = parseBoolean(
    import.meta.env.VITE_ENABLE_VUE_PLUGIN_CLAW_GUARD
  )

  /** 是否開啟 單號1000816 (最新消息版面的Line分享，攜帶內容) */
  const VITE_SHOW_1000816: boolean | undefined = parseBoolean(
    import.meta.env.VITE_SHOW_1000816
  )

  /** 是否開啟 G5 */
  const VITE_ENABLE_G5: boolean | undefined = parseBoolean(
    import.meta.env.VITE_ENABLE_G5
  )

  /** 是否開啟 G5.1 */
  const VITE_ENABLE_5_1: boolean | undefined = parseBoolean(
    import.meta.env.VITE_ENABLE_5_1
  )

  /** 是否開啟 1000123 */
  const VITE_ENABLE_1000123: boolean | undefined = parseBoolean(
    import.meta.env.VITE_ENABLE_1000123
  )

  /** GTM ID */
  const VITE_GTM_ID: string | undefined = z
    .string()
    .optional()
    .safeParse(import.meta.env.VITE_GTM_ID).data

  /** GTM 是否啟用 debug */
  const VITE_GTM_DEBUG: boolean | undefined = parseBoolean(
    import.meta.env.VITE_GTM_DEBUG
  )

  /** 後台網址 */
  const CMS_URL: string | undefined = z
    .string()
    .optional()
    .safeParse(import.meta.env.CMS_URL).data

  /** 長榮航空城市對航線資料 API - 單程/來回（Zh_TW） */
  const VITE_EVA_CITY_PAIR_ONLINE_ZH_TW_URL: string | undefined = z
    .string()
    .safeParse(import.meta.env.VITE_EVA_CITY_PAIR_ONLINE_ZH_TW_URL).data

  /** 長榮航空城市對航線資料 API - 單程/來回（Zh_CN） */
  const VITE_EVA_CITY_PAIR_ONLINE_ZH_CN_URL: string | undefined = z
    .string()
    .safeParse(import.meta.env.VITE_EVA_CITY_PAIR_ONLINE_ZH_CN_URL).data

  /** 長榮航空城市對航線資料 API - 單程/來回（En_US） */
  const VITE_EVA_CITY_PAIR_ONLINE_EN_US_URL: string | undefined = z
    .string()
    .safeParse(import.meta.env.VITE_EVA_CITY_PAIR_ONLINE_EN_US_URL).data

  /** 長榮航空城市對航線資料 API - 不同點進出/中途停留/多個城市（Zh_TW） */
  const VITE_EVA_CITY_PAIR_MULTI_ZH_TW_URL: string | undefined = z
    .string()
    .safeParse(import.meta.env.VITE_EVA_CITY_PAIR_MULTI_ZH_TW_URL).data

  /** 長榮航空城市對航線資料 API - 不同點進出/中途停留/多個城市（Zh_CN） */
  const VITE_EVA_CITY_PAIR_MULTI_ZH_CN_URL: string | undefined = z
    .string()
    .safeParse(import.meta.env.VITE_EVA_CITY_PAIR_MULTI_ZH_CN_URL).data

  /** 長榮航空城市對航線資料 API - 不同點進出/中途停留/多個城市（En_US） */
  const VITE_EVA_CITY_PAIR_MULTI_EN_US_URL: string | undefined = z
    .string()
    .safeParse(import.meta.env.VITE_EVA_CITY_PAIR_MULTI_EN_US_URL).data

  /** 長榮航空航班時刻表機場資料 API */
  const VITE_EVA_FLIGHT_SCHEDULES_URL: string | undefined = z
    .string()
    .safeParse(import.meta.env.VITE_EVA_FLIGHT_SCHEDULES_URL).data

  /** 長榮航空城市字典 API */
  const VITE_EVA_CITY_DICTIONARY_URL: string | undefined = z
    .string()
    .safeParse(import.meta.env.VITE_EVA_CITY_DICTIONARY_URL).data

  /** 長榮航空訂票平台 - 航班時刻表查詢頁面 URL */
  const VITE_EVA_BOOKING_FLIGHT_SCHEDULES_URL: string | undefined = z
    .string()
    .safeParse(import.meta.env.VITE_EVA_BOOKING_FLIGHT_SCHEDULES_URL).data

  return {
    /** 後台網址 */
    CMS_URL,
    /** 後端 API */
    VITE_API_BASE_URL,
    /** 長榮航空城市對航線資料 API（繁中，單程/來回） */
    VITE_EVA_CITY_PAIR_ONLINE_ZH_TW_URL,
    /** 長榮航空城市對航線資料 API（簡中，單程/來回） */
    VITE_EVA_CITY_PAIR_ONLINE_ZH_CN_URL,
    /** 長榮航空城市對航線資料 API（英文，單程/來回） */
    VITE_EVA_CITY_PAIR_ONLINE_EN_US_URL,
    /** 長榮航空城市對航線資料 API（繁中，多城市） */
    VITE_EVA_CITY_PAIR_MULTI_ZH_TW_URL,
    /** 長榮航空城市對航線資料 API（簡中，多城市） */
    VITE_EVA_CITY_PAIR_MULTI_ZH_CN_URL,
    /** 長榮航空城市對航線資料 API（英文，多城市） */
    VITE_EVA_CITY_PAIR_MULTI_EN_US_URL,
    /** 長榮航空航班時刻表機場資料 API */
    VITE_EVA_FLIGHT_SCHEDULES_URL,
    /** 長榮航空城市字典 API */
    VITE_EVA_CITY_DICTIONARY_URL,
    /** 長榮航空訂票平台航班時刻表查詢 URL */
    VITE_EVA_BOOKING_FLIGHT_SCHEDULES_URL,
    /** 前端 DEV 環境 VITE API Proxy 設定 */
    VITE_API_PROXY_URL,
    /** 前端版本號 */
    VITE_APPLICATION_VERSION,
    /** 前端版本號顯示方式 */
    VITE_APPLICATION_VERSION_MODE,
    /** 是否於 console 顯示 ZOD 錯誤 */
    VITE_SHOW_ZOD_ERROR,
    /** 是否顯示 1A 錯誤 */
    VITE_SHOW_1A_ERROR,
    /** Base Url */
    VITE_BASE_URL,
    /** 是否自動帶入驗證碼 */
    VITE_AUTO_INPUT_CAPTCHA_CODE,
    /** 儲存在瀏覽器的資料 Key 前綴 */
    VITE_STORAGE_PREFIX_KEY,
    /** 是否開啟 vue-plugin-claw-guard */
    VITE_ENABLE_VUE_PLUGIN_CLAW_GUARD,
    /** 是否開啟 G5 */
    VITE_ENABLE_G5,
    /** 是否開啟 G5.1 */
    VITE_ENABLE_5_1,
    /** 是否開啟 1000123 */
    VITE_ENABLE_1000123,
    /** 是否開啟 1000816 */
    VITE_SHOW_1000816,
    /** GTM ID */
    VITE_GTM_ID,
    /** GTM 是否啟用 debug */
    VITE_GTM_DEBUG
  }
}
